import { type Ref, ref } from 'vue'

import { ORDERS } from '@/constants'
import type { OrderStatusId } from '@/constants/domain/orders'
import type {
    InternalOrderCreateDraft,
    InternalOrderCreateState,
    InternalOrderDetails,
    InternalOrderFilters,
    InternalOrderListItem,
    InternalOrderStatus,
    NumberConfigurationTypeItem
} from '@/types/domain/orders/internal'

export function createDefaultStatusFilter(): string {
    return (Object.values(ORDERS.ORDER_STATUS) as OrderStatusId[]).filter((id) => id !== ORDERS.ORDER_STATUS.CLOSED).join(',')
}

export function createInitialFilters(): InternalOrderFilters {
    return {
        status_id: createDefaultStatusFilter(),
        search: null,
        customerName: null,
        createdBy_id: null,
        createdAt__gte: null,
        createdAt__lte: null,
        plannedSendDate__gte: null,
        plannedSendDate__lte: null,
        productionNumber: null,
        orderKind_id: ORDERS.ORDER_KIND.INTERNAL
    }
}

export function createEmptyOrderCreateOrder(): InternalOrderCreateDraft {
    return {
        sourceOrderId: null,
        name: null,
        numberConfigurationTypeId: null,
        customerId: null,
        customer: null,
        ownerId: null,
        netPrice: 0,
        valuation: 0,
        linkedToCampaign: false,
        orderProducts: [],
        plannedSendDate: null,
        plannedDeliveryDate: null,
        receiveMaterialDate: null,
        receiveFileDate: null,
        acceptFileDate: null,
        orderGraphicFileDeliveryMethodId: null,
        description: null,
        comments: [],
        orderDeliveryMethod: {
            methodId: null,
            cost: null,
            distance: null
        },
        parameters: [],
        deliveries: [],
        shopOrderId: -1,
        quantity: 0,
        numberOfTemplates: -1,
        grossPrice: 0,
        shopProductName: 'n/d',
        productPackageWeight: 0,
        productNumberOfPackages: 1,
        shopPrefix: 'CF',
        cooperant: 9,
        productPropertyValueSet: null
    }
}

export function createInternalOrdersState(): {
    orders: Ref<InternalOrderListItem[]>
    ordersLoading: Ref<boolean>
    filters: Ref<InternalOrderFilters>
    orderStatusList: Ref<InternalOrderStatus[]>
    order: Ref<InternalOrderDetails | null>
    numberConfigurationTypes: Ref<NumberConfigurationTypeItem[]>
    createInitialFilters: typeof createInitialFilters
    orderCreate: Ref<InternalOrderCreateState>
    createEmptyOrderCreateOrder: typeof createEmptyOrderCreateOrder
} {
    return {
        orders: ref<InternalOrderListItem[]>([]),
        ordersLoading: ref(false),
        filters: ref<InternalOrderFilters>(createInitialFilters()),
        orderStatusList: ref<InternalOrderStatus[]>([]),
        order: ref<InternalOrderDetails | null>(null),
        numberConfigurationTypes: ref<NumberConfigurationTypeItem[]>([]),
        createInitialFilters,
        orderCreate: ref<InternalOrderCreateState>({
            order: createEmptyOrderCreateOrder(),
            productTemplates: [],
            deliveryFile: null,
            adviceNoteDraft: null
        }),
        createEmptyOrderCreateOrder
    }
}

export type InternalOrdersState = ReturnType<typeof createInternalOrdersState>
